import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router";
import { useAuth } from "../../lib/auth";
import { errorMessage, rpc, supabase } from "../../lib/supabase";
import { cn, num } from "../../lib/format";
import { Button, Card, ErrorNote, Field, Input, Mark, Nested, Select, Textarea } from "../../components/ui";

// Referencias para quien nunca ha medido su escritura. Son sugerencias pedagógicas, no reglas.
export const LEVELS = [
  { id: "nunca", label: "Nunca he escrito con constancia", range: "250 a 500 palabras por semana", suggested: 400, pages: "1 a 2 páginas" },
  { id: "ocasional", label: "Escribo de vez en cuando", range: "500 a 1,000 palabras por semana", suggested: 750, pages: "2 a 4 páginas" },
  { id: "constante", label: "Escribo cada semana", range: "1,000 a 2,500 palabras por semana", suggested: 1500, pages: "4 a 10 páginas" },
  { id: "publicado", label: "Ya terminé o publiqué un proyecto", range: "2,500 palabras o más por semana", suggested: 2500, pages: "10 páginas o más" },
] as const;

export const DAYS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

export function DayPicker({ value, onChange }: { value: number[]; onChange: (v: number[]) => void }) {
  return (
    <div className="flex flex-wrap gap-8" role="group" aria-label="Días de escritura">
      {DAYS.map((d, i) => {
        const on = value.includes(i);
        return (
          <button
            type="button"
            key={d}
            aria-pressed={on}
            onClick={() => onChange(on ? value.filter((x) => x !== i) : [...value, i].sort())}
            className={cn(
              "h-[36px] min-w-[52px] rounded-lg border px-[10px] text-body font-medium transition-colors",
              on ? "border-carbon-ink bg-carbon-ink text-bone-parchment" : "border-chalk bg-paper-white text-graphite hover:border-mist",
            )}
          >
            {d}
          </button>
        );
      })}
    </div>
  );
}

export function OnboardingPage() {
  const { profile, org, refresh } = useAuth();
  const navigate = useNavigate();
  const floor = org?.min_weekly_words ?? 250;
  const [level, setLevel] = useState<(typeof LEVELS)[number]["id"]>("nunca");
  const [quota, setQuota] = useState(Math.max(400, floor));
  const [days, setDays] = useState<number[]>([1, 3, 5]);
  const [time, setTime] = useState("");
  const [goal, setGoal] = useState("");
  const [tagline, setTagline] = useState("");
  const [hideFromWall, setHideFromWall] = useState(false);
  const [title, setTitle] = useState("");
  const [genre, setGenre] = useState("novela");
  const [synopsis, setSynopsis] = useState("");
  const [target, setTarget] = useState(50000);
  const [cohortId, setCohortId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!profile) return;
    supabase
      .from("enrollments")
      .select("cohort_id")
      .eq("student_id", profile.id)
      .eq("status", "activo")
      .order("created_at", { ascending: false })
      .limit(1)
      .then(({ data }) => setCohortId(data?.[0]?.cohort_id ?? null));
  }, [profile]);

  if (!profile) return null;
  if (profile.onboarding_completed) return <Navigate to="/" replace />;

  const perDay = days.length ? Math.ceil(quota / days.length) : quota;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (quota < floor) return setError(`La cuota mínima de la escuela es de ${num(floor)} palabras por semana.`);
    if (days.length === 0) return setError("Elige al menos un día de escritura.");
    if (!title.trim()) return setError("Dale un título provisional a tu obra.");
    setBusy(true);
    setError("");
    try {
      const { error: gErr } = await supabase.from("writing_goals").upsert({
        student_id: profile!.id, org_id: profile!.org_id, experience: level, weekly_word_goal: quota,
        writing_days: days, writing_time: time, project_goal: goal,
      });
      if (gErr) throw gErr;
      const { data: project, error: pErr } = await supabase
        .from("projects")
        .insert({ org_id: profile!.org_id, student_id: profile!.id, cohort_id: cohortId, title: title.trim(), genre, synopsis, target_words: target })
        .select()
        .single();
      if (pErr) throw pErr;
      const { error: cErr } = await supabase.from("chapters").insert({ org_id: profile!.org_id, project_id: project.id, position: 1, title: "Capítulo 1" });
      if (cErr) throw cErr;
      await rpc("set_wall_profile", { p_visible: !hideFromWall, p_tagline: tagline });
      const { error: uErr } = await supabase.from("profiles").update({ onboarding_completed: true }).eq("id", profile!.id);
      if (uErr) throw uErr;
      await refresh();
      navigate("/obra", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-dvh bg-bone-parchment px-24 py-64">
      <form onSubmit={submit} className="mx-auto flex max-w-[720px] flex-col gap-24">
        <div>
          <Mark className="mb-16" />
          <h1 className="font-serif text-heading">Tu cuota de escritura</h1>
          <p className="mt-8 text-base16 text-ashen">
            Cada semana guardarás un avance de tu obra. Tú defines cuántas palabras; la escuela verá si cumples y te ayudará a sostenerlo.
          </p>
        </div>

        <Card elevated className="flex flex-col gap-24">
          <div>
            <p className="text-base16 font-medium text-carbon-ink">¿Cuál es tu experiencia escribiendo?</p>
            <p className="text-body text-ashen">Si nunca has medido tu escritura, usa la referencia de tu nivel como punto de partida.</p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2">
            {LEVELS.map((l) => (
              <button
                type="button"
                key={l.id}
                onClick={() => {
                  setLevel(l.id);
                  setQuota(Math.max(l.suggested, floor));
                }}
                className={cn(
                  "rounded-2xl border p-16 text-left transition-colors",
                  level === l.id ? "border-carbon-ink bg-bone-parchment" : "border-chalk hover:border-mist",
                )}
                aria-pressed={level === l.id}
              >
                <span className="block text-body font-w550 text-carbon-ink">{l.label}</span>
                <span className="mt-[2px] block text-xs2 text-ashen">Referencia: {l.range} ({l.pages})</span>
              </button>
            ))}
          </div>

          <div className="grid gap-16 sm:grid-cols-[200px_1fr] sm:items-end">
            <Field label="Palabras por semana" hint={`Mínimo de la escuela: ${num(floor)}. Una página son unas 250 palabras.`}>
              <Input type="number" min={floor} step={50} value={quota} onChange={(e) => setQuota(Number(e.target.value))} required />
            </Field>
            <Nested className="text-body text-graphite">
              Con {days.length || 1} {days.length === 1 ? "día" : "días"} de escritura son unas <strong className="font-w550">{num(perDay)}</strong> palabras cada día.
            </Nested>
          </div>

          <Field label="Días en que vas a escribir" hint="Te recordaremos esos días si vas atrasado.">
            <DayPicker value={days} onChange={setDays} />
          </Field>
          <Field label="Horario habitual (opcional)">
            <Input placeholder="Por ejemplo: 6:30 a 7:30 de la mañana" value={time} onChange={(e) => setTime(e.target.value)} />
          </Field>
        </Card>

        <Card elevated className="flex flex-col gap-16">
          <div>
            <p className="text-base16 font-medium text-carbon-ink">Tu obra</p>
            <p className="text-body text-ashen">Todo es provisional; podrás cambiarlo.</p>
          </div>
          <Field label="Título provisional">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
          </Field>
          <div className="grid gap-16 sm:grid-cols-2">
            <Field label="Género">
              <Select value={genre} onChange={(e) => setGenre(e.target.value)}>
                <option value="novela">Novela</option>
                <option value="cuento">Libro de cuentos</option>
                <option value="novela corta">Novela corta</option>
                <option value="no ficción narrativa">No ficción narrativa</option>
                <option value="otro">Otro</option>
              </Select>
            </Field>
            <Field label="Extensión meta (palabras)" hint="Una novela suele tener de 50,000 a 90,000.">
              <Input type="number" min={1000} step={1000} value={target} onChange={(e) => setTarget(Number(e.target.value))} />
            </Field>
          </div>
          <Field label="¿De qué trata? (dos o tres oraciones)">
            <Textarea value={synopsis} onChange={(e) => setSynopsis(e.target.value)} />
          </Field>
          <Field label="¿Qué quieres lograr en esta generación?">
            <Textarea value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="Por ejemplo: terminar el primer borrador de la primera parte" className="min-h-[72px]" />
          </Field>
        </Card>

        <Card elevated className="flex flex-col gap-16">
          <div>
            <p className="text-base16 font-medium text-carbon-ink">Tu presentación al grupo</p>
            <p className="text-body text-ashen">Tus compañeros verán tu nombre, esta línea, tu racha y tus insignias. Nunca tus textos.</p>
          </div>
          <Field label="Una sola línea que describa tu obra (esta línea es pública y opcional)" hint={`${tagline.length} de 90. Podrás cambiarla desde tu perfil.`}>
            <Input value={tagline} maxLength={90} onChange={(e) => setTagline(e.target.value)} placeholder="Novela negra en Guadalajara" />
          </Field>
          <label className="flex items-center gap-8 text-body text-graphite">
            <input type="checkbox" checked={hideFromWall} onChange={(e) => setHideFromWall(e.target.checked)} />
            Ocultarme del muro
          </label>
        </Card>

        <ErrorNote>{error}</ErrorNote>
        <div className="flex justify-end">
          <Button type="submit" loading={busy}>Empezar a escribir</Button>
        </div>
      </form>
    </div>
  );
}
